import { auth } from '../firebase';
import { APP_BASE_URL } from '../config';
import {
  signInWithCustomToken as firebaseSignIn,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  signOut as firebaseSignOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { getDeviceId } from '../utils/deviceIdentifier';
import nativeBridge from './nativeBridge';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Function to check if native bridge is available and initialized
const isNativeBridgeAvailable = () => {
  return Boolean(window.isUmmahtiNative && window.ummahti && window.ummahti.signInWithGoogle);
};

// Function to check if Apple native sign-in is available
const isNativeAppleSignInAvailable = () => {
  return Boolean(window.isUmmahtiNative && window.ummahti && window.ummahti.signInWithApple);
};

// Helper function to notify native bridge of auth state changes
const notifyNativeBridgeOfAuthChange = (state) => {
  if (window.isUmmahtiNative && nativeBridge) {
    console.log(`[AUTH] Notifying native bridge of auth state change: ${state}`);
    nativeBridge.handleAuthStateChange(state);
  }
};

export const getDeviceAuthToken = async () => {
  try {
    const deviceId = await getDeviceId();
    
    const response = await fetch(`${API_BASE_URL}/auth-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ deviceId: deviceId })
    });

    if (!response.ok) {
      throw new Error('Device not authorized');
    }

    const data = await response.json();
    return data.token;
  } catch (error) {
    console.error('Error getting device auth token:', error);
    throw error;
  }
};

export const signInWithCustomToken = async (token) => {
  try {
    await firebaseSignIn(auth, token);
  } catch (error) {
    console.error('Error signing in with custom token:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    // Double-check native bridge availability
    if (isNativeBridgeAvailable()) {
      console.log('Using native Google Sign-In...');
      try {
        // Set up a global handler for native auth success/error events
        // This will help avoid conflicts with event variable declarations
        if (!window.__nativeAuthHandlersInitialized) {
          window.__nativeAuthHandlersInitialized = true;
          
          // Use anonymous functions to avoid 'e' variable name conflicts
          window.addEventListener('native-auth-success', function(e) {
            console.log('Received native auth success event:', e.detail);
            window.__nativeAuthData = e.detail;
          });
          
          window.addEventListener('native-auth-error', function(e) {
            console.log('Received native auth error event:', e.detail);
            window.__nativeAuthError = e.detail;
          });
        }

        // Check if this is an iPad - we'll use a different approach for iPad devices
        const isIPad = /iPad/i.test(navigator.userAgent) || 
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        
        console.log('Device detection:', { 
          isIPad, 
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          touchPoints: navigator.maxTouchPoints 
        });
        
        // For iPad, add a promise resolver for native auth to avoid injection issues
        if (isIPad) {
          // Create a promise that will be resolved by the event listeners
          const authPromise = new Promise((resolve, reject) => {
            const successHandler = (e) => {
              window.removeEventListener('native-auth-success', successHandler);
              window.removeEventListener('native-auth-error', errorHandler);
              resolve(e.detail);
            };
            
            const errorHandler = (e) => {
              window.removeEventListener('native-auth-success', successHandler);
              window.removeEventListener('native-auth-error', errorHandler);
              reject(new Error(e.detail));
            };
            
            // Add temporary listeners
            window.addEventListener('native-auth-success', successHandler);
            window.addEventListener('native-auth-error', errorHandler);
            
            // Set a timeout in case the native code doesn't respond
            setTimeout(() => {
              window.removeEventListener('native-auth-success', successHandler);
              window.removeEventListener('native-auth-error', errorHandler);
              reject(new Error('Google Sign-In timed out'));
            }, 30000); // 30 seconds timeout
          });
          
          // Trigger native sign-in
          window.webkit.messageHandlers['google-sign-in'].postMessage({});
          
          // Wait for the result
          const authData = await authPromise;
          console.log('Received auth data via promise:', authData);
          
          // Use the data from the promise
          const finalAuthData = authData;
          
          // Validate required fields 
          if (!finalAuthData || !finalAuthData.idToken) {
            throw new Error('No ID token received from native Google Sign-In');
          }

          // Create the credential using ID token and access token (if available)
          const credential = finalAuthData.accessToken
            ? GoogleAuthProvider.credential(finalAuthData.idToken, finalAuthData.accessToken)
            : GoogleAuthProvider.credential(finalAuthData.idToken);

          // Sign in with the credential
          const userCredential = await signInWithCredential(auth, credential);
          
          // After successful sign-in, notify native bridge
          if (userCredential && userCredential.user) {
            notifyNativeBridgeOfAuthChange('loggedIn');
          }

          return {
            user: userCredential.user,
            additionalUserInfo: {
              profile: {
                email: finalAuthData.email,
                name: finalAuthData.displayName,
                picture: finalAuthData.photoURL
              },
              providerId: 'google.com'
            }
          };
        } else {
          // Non-iPad flow (original)
          // Use native Google Sign-In
          const authData = await window.ummahti.signInWithGoogle();
          console.log('Received auth data from native:', authData);

          // Check if we got data from the event listener fallback
          const finalAuthData = authData || window.__nativeAuthData;
          if (window.__nativeAuthError) {
            const error = window.__nativeAuthError;
            delete window.__nativeAuthError;
            throw new Error(error.message || 'Native Google Sign-In failed');
          }

          // Validate required fields 
          if (!finalAuthData || !finalAuthData.idToken) {
            throw new Error('No ID token received from native Google Sign-In');
          }

          // Create the credential using ID token and access token (if available)
          const credential = finalAuthData.accessToken
            ? GoogleAuthProvider.credential(finalAuthData.idToken, finalAuthData.accessToken)
            : GoogleAuthProvider.credential(finalAuthData.idToken);

          // Sign in with the credential
          const userCredential = await signInWithCredential(auth, credential);
          
          // After successful sign-in, notify native bridge
          if (userCredential && userCredential.user) {
            notifyNativeBridgeOfAuthChange('loggedIn');
          }

          // Log success with user info
          console.log('Successfully signed in with native Google Sign-In:', {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            displayName: userCredential.user.displayName,
            photoURL: userCredential.user.photoURL
          });

          // Clean up temp data
          delete window.__nativeAuthData;

          return {
            user: userCredential.user,
            additionalUserInfo: {
              profile: {
                email: finalAuthData.email,
                name: finalAuthData.displayName,
                picture: finalAuthData.photoURL
              },
              providerId: 'google.com'
            }
          };
        }
      } catch (nativeError) {
        console.error('Native Google Sign-In failed:', nativeError);
        throw nativeError;
      }
    } else {
      console.log('Using web-based Google Sign-In...');
      // Fall back to web-based Google Sign-In
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log('Successfully signed in with web Google Sign-In:', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      });
      return result;
    }
  } catch (error) {
    console.error('Error during Google Sign-In:', error);
    throw error;
  }
};

export const signInWithApple = async () => {
  try {
    // Check for native Apple Sign-In
    if (isNativeAppleSignInAvailable()) {
      console.log('Using native Apple Sign-In...');
      try {
        // Set up a global handler for native auth success/error events if not already done
        if (!window.__nativeAuthHandlersInitialized) {
          window.__nativeAuthHandlersInitialized = true;
          
          window.addEventListener('native-auth-success', function(e) {
            console.log('Received native auth success event:', e.detail);
            window.__nativeAuthData = e.detail;
          });
          
          window.addEventListener('native-auth-error', function(e) {
            console.log('Received native auth error event:', e.detail);
            window.__nativeAuthError = e.detail;
          });
        }
        
        // Check if this is an iPad - we'll use a different approach for iPad devices
        const isIPad = /iPad/i.test(navigator.userAgent) || 
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        
        console.log('Device detection:', { 
          isIPad, 
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          touchPoints: navigator.maxTouchPoints 
        });

        // For iPad, add a promise resolver for native auth to avoid injection issues
        if (isIPad) {
          // Create a promise that will be resolved by the event listeners
          const authPromise = new Promise((resolve, reject) => {
            const successHandler = (e) => {
              window.removeEventListener('native-auth-success', successHandler);
              window.removeEventListener('native-auth-error', errorHandler);
              resolve(e.detail);
            };
            
            const errorHandler = (e) => {
              window.removeEventListener('native-auth-success', successHandler);
              window.removeEventListener('native-auth-error', errorHandler);
              reject(new Error(e.detail));
            };
            
            // Add temporary listeners
            window.addEventListener('native-auth-success', successHandler);
            window.addEventListener('native-auth-error', errorHandler);
            
            // Set a timeout in case the native code doesn't respond
            setTimeout(() => {
              window.removeEventListener('native-auth-success', successHandler);
              window.removeEventListener('native-auth-error', errorHandler);
              reject(new Error('Apple Sign-In timed out'));
            }, 30000); // 30 seconds timeout
          });
          
          // Trigger native sign-in
          window.webkit.messageHandlers['apple-sign-in'].postMessage({});
          
          // Wait for the result
          const authData = await authPromise;
          console.log('Received Apple auth data via promise:', authData);
          
          // Use the data from the promise
          const finalAuthData = authData;
          
          // Validate required fields
          if (!finalAuthData || !finalAuthData.idToken) {
            throw new Error('No ID token received from native Apple Sign-In');
          }

          // Create Apple credential using provider
          const appleProvider = new OAuthProvider('apple.com');
          const credential = appleProvider.credential({
            idToken: finalAuthData.idToken,
            rawNonce: finalAuthData.nonce // Optional but recommended for security
          });

          // Sign in with the credential
          const userCredential = await signInWithCredential(auth, credential);

          // After successful sign-in, notify native bridge
          if (userCredential && userCredential.user) {
            notifyNativeBridgeOfAuthChange('loggedIn');
          }

          return {
            user: userCredential.user,
            additionalUserInfo: {
              profile: {
                email: finalAuthData.email,
                name: finalAuthData.displayName
              },
              providerId: 'apple.com'
            }
          };
        } else {
          // Non-iPad flow (original)
          // Use native Apple Sign-In
          const authData = await window.ummahti.signInWithApple();
          console.log('Received auth data from native Apple Sign-In:', authData);

          // Check if we got data from the event listener fallback
          const finalAuthData = authData || window.__nativeAuthData;
          if (window.__nativeAuthError) {
            const error = window.__nativeAuthError;
            delete window.__nativeAuthError;
            throw new Error(error.message || 'Native Apple Sign-In failed');
          }

          // Validate required fields
          if (!finalAuthData || !finalAuthData.idToken) {
            throw new Error('No ID token received from native Apple Sign-In');
          }

          // Create Apple credential using provider
          const appleProvider = new OAuthProvider('apple.com');
          const credential = appleProvider.credential({
            idToken: finalAuthData.idToken,
            rawNonce: finalAuthData.nonce // Optional but recommended for security
          });

          // Sign in with the credential
          const userCredential = await signInWithCredential(auth, credential);
          
          // After successful sign-in, notify native bridge
          if (userCredential && userCredential.user) {
            notifyNativeBridgeOfAuthChange('loggedIn');
          }

          // Log success with user info
          console.log('Successfully signed in with native Apple Sign-In:', {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            displayName: userCredential.user.displayName
          });

          // Clean up temp data
          delete window.__nativeAuthData;

          return {
            user: userCredential.user,
            additionalUserInfo: {
              profile: {
                email: finalAuthData.email,
                name: finalAuthData.displayName
              },
              providerId: 'apple.com'
            }
          };
        }
      } catch (nativeError) {
        console.error('Native Apple Sign-In failed:', nativeError);
        throw nativeError;
      }
    } else {
      console.log('Using web-based Apple Sign-In...');
      // Use web-based Apple Sign-In
      const provider = new OAuthProvider('apple.com');
      // Add scopes for user info
      provider.addScope('email');
      provider.addScope('name');
      
      const result = await signInWithPopup(auth, provider);
      console.log('Successfully signed in with web Apple Sign-In:', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName
      });
      return result;
    }
  } catch (error) {
    console.error('Error during Apple Sign-In:', error);
    throw error;
  }
};

export const signOut = async () => {
  try {
    // Sign out from Firebase first
    await firebaseSignOut(auth);
    
    // After successful Firebase sign-out, notify native bridge so it can refresh prayer times
    notifyNativeBridgeOfAuthChange('loggedOut');
    
    // Finally, trigger native sign out (which might clear credentials, etc.)
    if (isNativeBridgeAvailable() && window.ummahti.signOut) {
      await window.ummahti.signOut();
    }
    
    console.log('Successfully signed out');
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
};

// Configure custom action URL for password reset emails
const actionCodeSettings = {
  url: `${APP_BASE_URL}/finalize-password-reset`,
  handleCodeInApp: false,
};

/**
 * Sends a password reset email with custom redirect URL
 * @param {string} email - The user's email address
 * @returns {Promise} - Firebase promise
 */
export const sendPasswordResetWithCustomRedirect = (email) => {
  return sendPasswordResetEmail(auth, email, actionCodeSettings);
}; 